import { hashKey } from '@tanstack/react-query';
import {
  commercialJobCreateInput,
  quoteCreateInput,
  workOrderConfirmInput,
  workOrderTaskCreateInput,
  linkBackorderSupplyInput,
  commercialJobActivityCreateInput,
  type CommercialJobCreateInput,
  type QuoteCreateInput,
  type WorkOrderConfirmInput,
  type WorkOrderTaskCreateInput,
  type LinkBackorderSupplyInput,
  type CommercialJobActivityCreateInput,
} from '@servwell/shared';
import { sameAccount, type AccountRef } from './account';
import { idbKv, type Kv } from './browser';

export type CommercialRequests = {
  'commercialJob.create': CommercialJobCreateInput;
  'quote.create': QuoteCreateInput;
  'workOrder.confirm': WorkOrderConfirmInput;
  'workOrder.taskCreate': WorkOrderTaskCreateInput;
  'inventoryReservations.linkSupply': LinkBackorderSupplyInput;
  'commercialJob.addActivity': CommercialJobActivityCreateInput;
};
export type CommercialAction = keyof CommercialRequests;
export type CommercialWriteRead<A extends CommercialAction> =
  | { status: 'empty' }
  | { status: 'unreadable' }
  | { status: 'ready'; request: CommercialRequests[A] };

const schemas = {
  'commercialJob.create': commercialJobCreateInput,
  'quote.create': quoteCreateInput,
  'workOrder.confirm': workOrderConfirmInput,
  'workOrder.taskCreate': workOrderTaskCreateInput,
  'inventoryReservations.linkSupply': linkBackorderSupplyInput,
  'commercialJob.addActivity': commercialJobActivityCreateInput,
};
const slot = (owner: AccountRef, action: CommercialAction, subject: string) =>
  `servwell-commercial-write:${JSON.stringify([owner.orgId, owner.userId, action, subject])}`;

export function commercialRequestIssue<A extends CommercialAction>(
  action: A,
  request: CommercialRequests[A],
): string | null {
  const parsed = schemas[action].safeParse(request);
  return parsed.success
    ? null
    : (parsed.error.issues[0]?.message ??
        'Complete the required fields before submitting.');
}

function parseRequest<A extends CommercialAction>(
  action: A,
  subject: string,
  value: unknown,
): CommercialRequests[A] | null {
  const parsed = schemas[action].safeParse(value);
  if (!parsed.success || !parsed.data.clientId || !parsed.data.mutationId) return null;
  if (
    action === 'workOrder.confirm' &&
    (parsed.data as WorkOrderConfirmInput).quoteId !== subject
  )
    return null;
  if (action === 'quote.create' && !(parsed.data as QuoteCreateInput).commercialJobId)
    return null;
  if (
    action === 'workOrder.taskCreate' &&
    (parsed.data as WorkOrderTaskCreateInput).workOrderId !== subject
  )
    return null;
  if (
    action === 'inventoryReservations.linkSupply' &&
    (parsed.data as LinkBackorderSupplyInput).workOrderLineId !== subject
  )
    return null;
  if (
    action === 'commercialJob.addActivity' &&
    (parsed.data as CommercialJobActivityCreateInput).commercialJobId !== subject
  )
    return null;
  return parsed.data as CommercialRequests[A];
}

function readValue<A extends CommercialAction>(
  owner: AccountRef,
  action: A,
  subject: string,
  value: unknown,
): CommercialWriteRead<A> {
  if (value === undefined) return { status: 'empty' };
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return { status: 'unreadable' };
  const row = value as Record<string, unknown>;
  if (
    Object.keys(row).some(
      (key) => !['owner', 'action', 'subject', 'request'].includes(key),
    ) ||
    !sameAccount(row.owner as AccountRef, owner) ||
    row.action !== action ||
    row.subject !== subject
  )
    return { status: 'unreadable' };
  const request = parseRequest(action, subject, row.request);
  return request ? { status: 'ready', request } : { status: 'unreadable' };
}

export async function readCommercialWrite<A extends CommercialAction>(
  owner: AccountRef,
  action: A,
  subject: string,
  kv: Kv = idbKv,
): Promise<CommercialWriteRead<A>> {
  try {
    return readValue(owner, action, subject, await kv.get(slot(owner, action, subject)));
  } catch {
    return { status: 'unreadable' };
  }
}

/** IndexedDB update is one readwrite transaction across tabs. Persist BEFORE calling the API. */
export async function reserveCommercialWrite<A extends CommercialAction>(
  owner: AccountRef,
  action: A,
  subject: string,
  input: CommercialRequests[A],
  kv: Kv = idbKv,
): Promise<boolean> {
  const request = parseRequest(action, subject, input);
  if (!request) return false;
  const same = (other: CommercialRequests[A]) => hashKey([other]) === hashKey([request]);
  try {
    await kv.update(slot(owner, action, subject), (value) => {
      const current = readValue(owner, action, subject, value);
      if (
        current.status === 'unreadable' ||
        (current.status === 'ready' && !same(current.request))
      )
        throw new Error('Another unresolved request must be recovered first.');
      return current.status === 'ready'
        ? value
        : {
            owner: { orgId: owner.orgId, userId: owner.userId },
            action,
            subject,
            request,
          };
    });
    const saved = await readCommercialWrite(owner, action, subject, kv);
    return saved.status === 'ready' && same(saved.request);
  } catch {
    return false;
  }
}

/** Success clears only its own receipt. A new tab's request can never be removed by an old response. */
export async function clearCommercialWrite(
  owner: AccountRef,
  action: CommercialAction,
  subject: string,
  mutationId: string,
  kv: Kv = idbKv,
): Promise<boolean> {
  try {
    await kv.update(slot(owner, action, subject), (value) => {
      const current = readValue(owner, action, subject, value);
      if (current.status === 'empty') return undefined;
      if (current.status !== 'ready' || current.request.mutationId !== mutationId)
        throw new Error('Saved request changed.');
      return undefined;
    });
    return (await readCommercialWrite(owner, action, subject, kv)).status === 'empty';
  } catch {
    return false;
  }
}
